import * as zod from 'zod';

import { CameraIntegration } from '@sb/integrations/types/cameraTypes';
import { ActionRequiredError, Step } from '@sb/remote-control/types';
import { validateCodeExpression } from '@sb/remote-control/util/expressions/validateCodeExpression';
import {
  DEFAULT_REGION_OF_INTEREST,
  Expression,
  RegionOfInterest,
} from '@sb/routine-runner';

export namespace CaptureStep {
  export const name = 'Capture';
  export const description =
    'Capture and save a single frame from the camera stream';
  export const librarySection = Step.LibrarySection.Vision;
  export const argumentKind = 'Capture';
  export const deviceKinds = [
    'WristCamera',
    'Camera',
    'FTPServer',
    'S3Bucket',
  ] as const;

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    cameraEquipmentId: zod.string().optional(),
    camera: CameraIntegration.optional(),
    regionOfInterest: RegionOfInterest.default(
      DEFAULT_REGION_OF_INTEREST,
    ).optional(),
    saveEquipmentId: zod.string(),
    saveFilepath: Expression,
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const validator: Step.Validator = ({ stepConfiguration }) => {
    const args = stepConfiguration?.args;

    if (args?.argumentKind !== argumentKind) {
      return;
    }

    if (!args.saveEquipmentId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Save equipment ID is required',
        fieldId: 'saveEquipmentId',
      });
    }

    // Must be a non-empty string
    if (
      args.saveFilepath &&
      args.saveFilepath.kind === 'constant' &&
      args.saveFilepath.value === ''
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Save file path is required',
        fieldId: 'saveFilepath',
      });
    }

    // Must end in .png
    if (
      (args.saveFilepath.kind === 'constant' &&
        !args.saveFilepath.value.toString().endsWith('.png')) ||
      (args.saveFilepath.kind === 'JavaScript' &&
        !args.saveFilepath.expression.endsWith('.png"')) ||
      (args.saveFilepath.kind === 'Python' &&
        !args.saveFilepath.expression.endsWith('.png"'))
    ) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Save file path must end with .png',
        fieldId: 'saveFilepath',
      });
    }

    validateCodeExpression(args.saveFilepath, 'saveFilepath', 'Save filepath');
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'Capture',
      args: {
        camera: args.camera,
        cameraEquipmentId: args.cameraEquipmentId,
        regionOfInterest: args.regionOfInterest,
        saveEquipmentId: args.saveEquipmentId,
        saveFilepath: args.saveFilepath,
      },
    };
  };
}

CaptureStep satisfies Step.StepKindInfo;
